import { track } from '../../../../services/analytics/analytics'

// Types for tracking events
interface BaseEventProperties {
  timestamp?: string
  session_id?: string
  [key: string]: string | string[] | number | boolean | undefined | null
}

interface LeavePoliciesOverviewEngagedProperties extends BaseEventProperties {
  milestone_type: 'time_spent' | 'interactions' | 'sections_explored'
  milestone_value: number
  current_section: string
  actions_completed: number
  data_freshness: 'fresh' | 'partial' | 'stale'
  scroll_depth: number
  unique_sections_visited: number
  meaningful_interactions: number
  total_policies_count: number
  filtered_policies_count: number
  has_active_filters: boolean
  filters_applied: string[]
}

interface LeavePolicyViewedProperties extends BaseEventProperties {
  time_of_day: 'morning' | 'afternoon' | 'evening'
  day_of_week: string
  is_weekend: boolean
  is_mobile: boolean
  viewport_width: number
  viewport_height: number
  total_policies_count: number
  referrer: string
}

interface LeavePolicyEditedProperties extends BaseEventProperties {
  policy_id: string
  policy_name: string
  leave_type_id: string
  leave_type_name: string
  location_id: string
  location_name: string
  time_since_page_load_ms: number
  had_active_filters: boolean
  interaction_method: 'table_row_click' | 'edit_icon_click'
}

interface LeavePolicySortedProperties extends BaseEventProperties {
  column_name: string
  sort_direction: 'ascend' | 'descend' | null
  total_policies_displayed: number
  had_active_filters: boolean
  time_since_page_load_ms: number
}

interface LeavePolicyPaginatedProperties extends BaseEventProperties {
  interaction_type: 'page_change' | 'page_size_change' | 'quick_jumper'
  from_page?: number
  to_page?: number
  from_page_size?: number
  to_page_size?: number
  total_policies_count: number
  filtered_policies_count: number
  had_active_filters: boolean
  time_since_page_load_ms: number
}

interface LeavePolicyFilteredProperties extends BaseEventProperties {
  filter_type: 'location' | 'leave_type' | 'both' | 'cleared'
  locations_selected: number
  leave_types_selected: number
  total_filters_applied: number
  total_policies_count: number
  filtered_policies_count: number
  filter_reduction_percentage: number
  time_since_page_load_ms: number
}

// Session tracking state
class LeavePoliciesOverviewSessionTracker {
  private sessionStartTime: number = Date.now()
  private sessionId = `leave_policies_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  private pageLoadTime: number = Date.now()
  private sectionsViewed: Set<string> = new Set()
  private actionsTaken: Set<string> = new Set()
  private interactionCount = 0
  private maxScrollDepth = 0

  // Store timeout IDs and event handlers for cleanup
  private timeouts: NodeJS.Timeout[] = []
  private eventHandlers: { event: string; handler: () => void }[] = []

  // Store current page data
  private totalPoliciesCount = 0
  private filteredPoliciesCount = 0
  private activeFilters: string[] = []

  // Helper methods
  private getTimeOfDay(): 'morning' | 'afternoon' | 'evening' {
    const hour = new Date().getHours()
    if (hour < 12) return 'morning'
    if (hour < 17) return 'afternoon'
    return 'evening'
  }

  private getDayOfWeek(): string {
    return new Date().toLocaleDateString('en-US', { weekday: 'long' })
  }

  private isWeekend(): boolean {
    const day = new Date().getDay()
    return day === 0 || day === 6
  }

  private isMobile(): boolean {
    return window.innerWidth < 768
  }

  private updateScrollDepth(): void {
    const scrollDepth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100)
    this.maxScrollDepth = Math.max(this.maxScrollDepth, scrollDepth || 0)
  }

  // Update page data for engagement tracking
  updatePageData(data: {
    totalPoliciesCount: number
    filteredPoliciesCount: number
    activeFilters: string[]
  }): void {
    this.totalPoliciesCount = data.totalPoliciesCount
    this.filteredPoliciesCount = data.filteredPoliciesCount
    this.activeFilters = data.activeFilters
  }

  // Track section view
  trackSectionView(section: string): void {
    this.sectionsViewed.add(section)
  }

  // Track action
  trackAction(action: string): void {
    this.actionsTaken.add(action)
    this.interactionCount++
  }

  // Public tracking methods
  trackViewed(viewData: {
    totalPoliciesCount: number
  }): void {
    const properties: LeavePolicyViewedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      time_of_day: this.getTimeOfDay(),
      day_of_week: this.getDayOfWeek(),
      is_weekend: this.isWeekend(),
      is_mobile: this.isMobile(),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      total_policies_count: viewData.totalPoliciesCount,
      referrer: document.referrer || 'direct',
    }

    track('LEAVE_POLICY_VIEWED', properties)
  }

  trackEdited(editData: {
    policyId: string
    policyName: string
    leaveTypeId: string
    leaveTypeName: string
    locationId: string
    locationName: string
    interactionMethod: 'table_row_click' | 'edit_icon_click'
  }): void {
    this.interactionCount++
    this.actionsTaken.add('policy_edited')

    const properties: LeavePolicyEditedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      policy_id: editData.policyId,
      policy_name: editData.policyName,
      leave_type_id: editData.leaveTypeId,
      leave_type_name: editData.leaveTypeName,
      location_id: editData.locationId,
      location_name: editData.locationName,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
      had_active_filters: this.activeFilters.length > 0,
      interaction_method: editData.interactionMethod,
    }

    track('LEAVE_POLICY_EDITED', properties)
  }

  trackSorted(sortData: {
    columnName: string
    sortDirection: 'ascend' | 'descend' | null
    totalPoliciesDisplayed: number
  }): void {
    this.interactionCount++
    this.actionsTaken.add('policy_sorted')

    const properties: LeavePolicySortedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      column_name: sortData.columnName,
      sort_direction: sortData.sortDirection,
      total_policies_displayed: sortData.totalPoliciesDisplayed,
      had_active_filters: this.activeFilters.length > 0,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('LEAVE_POLICY_SORTED', properties)
  }

  trackPaginated(paginationData: {
    interactionType: 'page_change' | 'page_size_change' | 'quick_jumper'
    fromPage?: number
    toPage?: number
    fromPageSize?: number
    toPageSize?: number
  }): void {
    this.interactionCount++
    this.actionsTaken.add('policy_paginated')

    const properties: LeavePolicyPaginatedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      interaction_type: paginationData.interactionType,
      from_page: paginationData.fromPage,
      to_page: paginationData.toPage,
      from_page_size: paginationData.fromPageSize,
      to_page_size: paginationData.toPageSize,
      total_policies_count: this.totalPoliciesCount,
      filtered_policies_count: this.filteredPoliciesCount,
      had_active_filters: this.activeFilters.length > 0,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('LEAVE_POLICY_PAGINATED', properties)
  }

  trackFiltered(filterData: {
    filterType: 'location' | 'leave_type' | 'both' | 'cleared'
    locationsSelected: number
    leaveTypesSelected: number
    totalPoliciesCount: number
    filteredPoliciesCount: number
  }): void {
    this.interactionCount++
    this.actionsTaken.add('policy_filtered')

    const totalFilters = filterData.locationsSelected + filterData.leaveTypesSelected
    const filterReduction = filterData.totalPoliciesCount > 0
      ? Math.round(((filterData.totalPoliciesCount - filterData.filteredPoliciesCount) / filterData.totalPoliciesCount) * 100)
      : 0

    const properties: LeavePolicyFilteredProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      filter_type: filterData.filterType,
      locations_selected: filterData.locationsSelected,
      leave_types_selected: filterData.leaveTypesSelected,
      total_filters_applied: totalFilters,
      total_policies_count: filterData.totalPoliciesCount,
      filtered_policies_count: filterData.filteredPoliciesCount,
      filter_reduction_percentage: filterReduction,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('LEAVE_POLICY_FILTERED', properties)
  }

  trackEngaged(milestoneData: {
    milestoneType: 'time_spent' | 'interactions' | 'sections_explored'
    milestoneValue: number
    currentSection: string
    actionsCompleted: number
    dataFreshness: 'fresh' | 'partial' | 'stale'
  }): void {
    this.updateScrollDepth()

    const properties: LeavePoliciesOverviewEngagedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      milestone_type: milestoneData.milestoneType,
      milestone_value: milestoneData.milestoneValue,
      current_section: milestoneData.currentSection,
      actions_completed: milestoneData.actionsCompleted,
      data_freshness: milestoneData.dataFreshness,
      scroll_depth: this.maxScrollDepth,
      unique_sections_visited: this.sectionsViewed.size,
      meaningful_interactions: this.interactionCount,
      total_policies_count: this.totalPoliciesCount,
      filtered_policies_count: this.filteredPoliciesCount,
      has_active_filters: this.activeFilters.length > 0,
      filters_applied: this.activeFilters,
    }

    track('LEAVE_POLICIES_OVERVIEW_ENGAGED', properties)
  }

  // Initialize tracking on page load
  init(): void {
    // Track initial page load
    this.pageLoadTime = Date.now()

    // Set up scroll tracking
    const scrollHandler = () => {
      this.updateScrollDepth()
    }
    window.addEventListener('scroll', scrollHandler)
    this.eventHandlers.push({ event: 'scroll', handler: scrollHandler })

    // Set up engagement milestones
    const timeout30s = setTimeout(() => this.trackEngaged({
      milestoneType: 'time_spent',
      milestoneValue: 30,
      currentSection: 'leave_policies_overview',
      actionsCompleted: this.interactionCount,
      dataFreshness: 'fresh',
    }), 30000) // 30 seconds
    this.timeouts.push(timeout30s)

    const timeout2m = setTimeout(() => this.trackEngaged({
      milestoneType: 'time_spent',
      milestoneValue: 120,
      currentSection: 'leave_policies_overview',
      actionsCompleted: this.interactionCount,
      dataFreshness: 'fresh',
    }), 120000) // 2 minutes
    this.timeouts.push(timeout2m)
  }

  // Cleanup method to cancel all timeouts and remove event listeners
  cleanup(): void {
    // Clear all timeouts
    this.timeouts.forEach(timeout => clearTimeout(timeout))
    this.timeouts = []

    // Remove all event listeners
    this.eventHandlers.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler)
    })
    this.eventHandlers = []
  }
}

// Create and export singleton instance
export const leavePoliciesOverviewTracker = new LeavePoliciesOverviewSessionTracker()


